import { isDublinId } from "./branchUtils"
import {
  bedfordBranchId,
  birminghamBranchId,
  dublCorkBranchId,
  egsBranchId,
  fewBranchId,
  fortusEwBranchId,
  londonBranchId,
  norwichBranchId,
  sspBranchId,
} from "./constants"
import { currencyNameToSymbol } from "../website/utils/currency"
import { formatPrice } from "../website/utils/utils"

export type OperatingRegion = "uk" | "ireland"

const irelandBranchIds = new Set([dublCorkBranchId])

export function getOperatingRegionForBranchId(branchId: string): OperatingRegion {
  return isDublinId(branchId) || irelandBranchIds.has(branchId) ? "ireland" : "uk"
}

export function branchIdsForOperatingRegion(region: OperatingRegion): string[] {
  if (region === "ireland") {
    return [dublCorkBranchId]
  }
  return [
    fewBranchId,
    fortusEwBranchId,
    norwichBranchId,
    birminghamBranchId,
    bedfordBranchId,
    sspBranchId,
    egsBranchId,
    londonBranchId,
  ]
}

export type IClearancePrice = {
  price_pennies: number
  was_price_pennies: number | null
  currency_name: string
}

const clearanceCurrencyNameForOperatingRegion: Record<OperatingRegion, string> = {
  uk: "Pound Sterling",
  ireland: "Euro",
}

export function getClearancePriceForProduct(product: {
  branch?: { id: string } | null
  product_master?: {
    clearance_price_uk_pennies?: number | null
    clearance_price_ireland_pennies?: number | null
    clearance_was_price_uk_pennies?: number | null
    clearance_was_price_ireland_pennies?: number | null
  } | null
}): IClearancePrice | null {
  const branchId = product.branch?.id
  const master = product.product_master
  if (!branchId || !master) {
    return null
  }

  return getClearancePriceForProductMaster(master, branchId)
}

export function getClearancePriceForProductMaster(
  productMaster: {
    clearance_price_uk_pennies?: number | null
    clearance_price_ireland_pennies?: number | null
    clearance_was_price_uk_pennies?: number | null
    clearance_was_price_ireland_pennies?: number | null
  },
  branchId: string,
): IClearancePrice | null {
  const operatingRegion = getOperatingRegionForBranchId(branchId)
  const clearancePricePennies =
    operatingRegion === "ireland"
      ? productMaster.clearance_price_ireland_pennies
      : productMaster.clearance_price_uk_pennies

  if (clearancePricePennies == null || clearancePricePennies <= 0) {
    return null
  }

  const wasPricePennies =
    operatingRegion === "ireland"
      ? productMaster.clearance_was_price_ireland_pennies
      : productMaster.clearance_was_price_uk_pennies

  return {
    price_pennies: clearancePricePennies,
    was_price_pennies: wasPricePennies ?? null,
    currency_name: clearanceCurrencyNameForOperatingRegion[operatingRegion],
  }
}

export function formatClearancePrice(clearancePrice: IClearancePrice): string {
  return formatPrice(clearancePrice.price_pennies, {
    isPennies: true,
    currency: currencyNameToSymbol(clearancePrice.currency_name),
  })
}

export function formatClearancePriceForProduct(
  product: Parameters<typeof getClearancePriceForProduct>[0],
) {
  const clearancePrice = getClearancePriceForProduct(product)
  return clearancePrice ? formatClearancePrice(clearancePrice) : null
}

export function getClearancePriceAttributeForOperatingRegion(
  region: OperatingRegion,
): "clearance_price_uk_pennies" | "clearance_price_ireland_pennies" {
  return region === "ireland" ? "clearance_price_ireland_pennies" : "clearance_price_uk_pennies"
}

export function getClearancePriceAttributeForBranchId(
  branchId: string,
): "clearance_price_uk_pennies" | "clearance_price_ireland_pennies" {
  return getClearancePriceAttributeForOperatingRegion(getOperatingRegionForBranchId(branchId))
}

/** Skeem filter: product masters with a regional clearance price and in-stock regional free stock. */
export function inStockClearanceProductMastersFilter(branchId: string) {
  const clearanceField = getClearancePriceAttributeForBranchId(branchId)
  const regionalBranchIds = branchIdsForOperatingRegion(getOperatingRegionForBranchId(branchId))
  return {
    and: [
      { matches: { hidden: false } },
      { matches: { [clearanceField]: { gt: 0 } } },
      {
        gte: [
          {
            count: {
              path: [
                {
                  name: "products",
                  filter: { matches: { branch: { or: regionalBranchIds } } },
                },
                {
                  name: "product_locations",
                  filter: { matches: { free_stock: { gt: 0 } } },
                },
              ],
            },
          },
          { value: 1 },
        ],
      },
    ],
  }
}

/** Skeem filter: exclude clearance masters with no in-stock product_locations in the operating region. */
export function hideClearanceWithZeroRegionalStockFilter(branchId: string | null | undefined) {
  if (!branchId) {
    return undefined
  }
  const clearanceField = getClearancePriceAttributeForBranchId(branchId)
  const regionalBranchIds = branchIdsForOperatingRegion(getOperatingRegionForBranchId(branchId))
  return {
    or: [
      { not: { matches: { [clearanceField]: { gt: 0 } } } },
      {
        gte: [
          {
            count: {
              path: [
                {
                  name: "products",
                  filter: { matches: { branch: { or: regionalBranchIds } } },
                },
                {
                  name: "product_locations",
                  filter: { matches: { free_stock: { gt: 0 } } },
                },
              ],
            },
          },
          { value: 1 },
        ],
      },
    ],
  }
}

export type ProductMasterCustomerTag = "clearance" | "discontinued"
